// ServiceCard/index.tsx
import React, { useState } from 'react';
import { ServiceCardHeader } from './components/ServiceCardHeader';
import { ReadingsSection } from './components/ReadingsSection';
import SongIdeasSection from './components/SongIdeasSection';
import { SongsSection } from './components/SongsSection';
import { getLiturgicalClass } from './utils/dateUtils';
import { useServiceEditing } from './hooks/useServiceEditing';
import { Service, ServiceSelection, Reading } from '../types/planningTypes';
import { SpanishSelection } from '../types/songTypes';
import styles from './styles/liturgical.module.css';
import EditSelectionsModal from '../EditSelectionsModal';
import { HistoryDrawer } from '../HistoryDrawer';
import { PlanningNoticeStrip } from '../PlanningNotices';

interface ServiceCardProps {
  service: Service;
  selections: ServiceSelection[];
  spanishSelections?: SpanishSelection[];
  reading?: Reading;
  onUpdate: (service: Service) => void;
  onSelectionsUpdate: (serviceId: number, updatedSelections: ServiceSelection[]) => void;
  filterState?: {
    hideServiceMusic: boolean;
    selectedPerformanceTypes: number[];
  };
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  selections,
  spanishSelections = [],
  reading,
  onUpdate,
  onSelectionsUpdate,
  filterState = { hideServiceMusic: false, selectedPerformanceTypes: [] }
}) => {
  const [showSelectionsModal, setShowSelectionsModal] = useState(false);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  
  const { handleEditSongs } = useServiceEditing({
    serviceId: service.service_id,
    onEditSongs: () => {
      setShowSelectionsModal(true);
    }
  });

  const handleSelectionsUpdate = async (updatedSelections: ServiceSelection[]) => {
    // Remove the direct state update
    await onSelectionsUpdate(service.service_id, updatedSelections);
    setShowSelectionsModal(false);
  };

  const liturgicalClass = getLiturgicalClass(service.liturgical_season);

  return (
    <div className={styles.wrapper}>
      <div className={`${styles.card} ${styles[liturgicalClass]}`}>
        <div className={styles.cardHeader}>
          <ServiceCardHeader
            liturgyDate={service.liturgy_date}
            liturgyName={service.liturgy_name || 'Service'}
            liturgicalSeason={service.liturgical_season}
            time={service.time}
            onHistoryClick={() => setShowHistoryDrawer(true)}
          />
        </div>
        <PlanningNoticeStrip serviceId={service.service_id} />
        <div className={styles.cardBody}>
          <div className="service-card-content">
            <div className="readings-section">
              <ReadingsSection
                serviceId={service.service_id}
                readings={{
                  ot_reading: service.ot_reading,
                  nt_reading: service.nt_reading,
                  gospel_reading: service.gospel_reading,
                  psalm_reading: service.psalm_reading
                }}
                liturgicalClass={liturgicalClass}
                liturgyName={service.liturgy_name || 'Service'}
                liturgyDate={service.liturgy_date}
                onReadingsUpdate={onUpdate}
              />
              <SongIdeasSection
                serviceId={service.service_id}
                songIdeas={service.song_ideas || []}
                liturgicalClass={liturgicalClass}
                onSongIdeasUpdate={onUpdate}
              />
            </div>
            <SongsSection
              serviceId={service.service_id}
              selections={selections}
              spanishSelections={spanishSelections}
              onEdit={handleEditSongs}
              liturgicalClass={liturgicalClass}
              filterState={filterState}
            />
          </div>
        </div>
      </div>

      <EditSelectionsModal
        show={showSelectionsModal}
        onHide={() => setShowSelectionsModal(false)}
        serviceId={service.service_id}
        liturgyName={service.liturgy_name}
        selections={selections}
        onUpdate={handleSelectionsUpdate}
      />

      <HistoryDrawer
        isOpen={showHistoryDrawer}
        onClose={() => setShowHistoryDrawer(false)}
        liturgyName={service.liturgy_name || ''}
        season={service.liturgical_season}
      />
    </div>
  );
};

export default ServiceCard;